<script setup>
import { ref, computed, onMounted } from 'vue';
import { useIntegrationHook } from 'dashboard/composables/useIntegrationHook';
import { useBranding } from 'shared/composables/useBranding';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import MedupFeedAPI from 'dashboard/api/medupFeed';

const props = defineProps({
  integrationId: {
    type: String,
    required: true,
  },
});

defineEmits(['add', 'delete', 'edit']);

const { integration, hasConnectedHooks } = useIntegrationHook(
  props.integrationId
);

const { replaceInstallationName } = useBranding();

// --- MedUp deal feeder (event stream) controls, shown only on the MedUp connector ---
const isMedup = computed(
  () => props.integrationId === 'medup' && hasConnectedHooks.value
);
const feedStatus = ref(null);
const feeding = ref(false);

const loadStatus = async () => {
  if (!isMedup.value) return;
  try {
    const { data } = await MedupFeedAPI.status();
    feedStatus.value = data;
  } catch (e) {
    /* status is best-effort */
  }
};
onMounted(loadStatus);

const runFeed = async () => {
  feeding.value = true;
  try {
    await MedupFeedAPI.feed();
    useAlert('Фидер запущен — события появятся в «Поток событий»');
  } catch (e) {
    useAlert('Не удалось запустить фидер');
  } finally {
    feeding.value = false;
  }
};

const feedStats = computed(() => feedStatus.value?.deal_feed_stats || null);
</script>

<template>
  <div
    class="outline outline-n-container outline-1 bg-n-card rounded-xl flex-grow overflow-auto p-4"
  >
    <div class="flex items-center justify-center">
      <div class="flex h-16 w-16 items-center justify-center">
        <img
          :src="`/dashboard/images/integrations/${integrationId}.png`"
          class="max-w-full rounded-md border border-n-weak shadow-sm block dark:hidden bg-n-alpha-3 dark:bg-n-alpha-2"
        />
        <img
          :src="`/dashboard/images/integrations/${integrationId}-dark.png`"
          class="max-w-full rounded-md border border-n-weak shadow-sm hidden dark:block bg-n-alpha-3 dark:bg-n-alpha-2"
        />
      </div>
      <div class="flex flex-col justify-center m-0 mx-4 flex-1">
        <h3 class="mb-1 text-heading-1 text-n-slate-12">
          {{ integration.name }}
        </h3>
        <p class="text-n-slate-11 text-body-main">
          {{ replaceInstallationName(integration.description) }}
        </p>
      </div>
      <div class="flex justify-center items-center mb-0 gap-2">
        <div v-if="hasConnectedHooks" class="flex items-center gap-2">
          <Button
            blue
            faded
            label="Редактировать"
            @click="$emit('edit', integration.hooks[0])"
          />
          <Button
            ruby
            faded
            :label="$t('INTEGRATION_APPS.DISCONNECT.BUTTON_TEXT')"
            @click="$emit('delete', integration.hooks[0])"
          />
        </div>
        <div v-else>
          <Button
            blue
            faded
            :label="$t('INTEGRATION_APPS.CONNECT.BUTTON_TEXT')"
            @click="$emit('add')"
          />
        </div>
      </div>
    </div>

    <!-- MedUp feeder: pulls deals from MRES and emits events into the stream (read-only to MRES) -->
    <div
      v-if="isMedup"
      class="mt-4 pt-4 border-t border-n-weak flex items-center justify-between gap-3"
    >
      <div class="text-sm">
        <div class="font-medium text-n-slate-12">Фидер сделок (поток событий)</div>
        <div class="text-n-slate-11">
          Тянет сделки из MRES и пишет события в поток. В MRES ничего не отправляется.
        </div>
        <div v-if="feedStatus" class="mt-1 text-xs text-n-slate-10">
          Последний прогон: {{ feedStatus.deal_feed_at || '—' }}
          <template v-if="feedStats">
            · новых: {{ feedStats.created || 0 }} · смен стадий:
            {{ feedStats.stage_changed || 0 }} · перенос:
            {{ feedStats.rescheduled || 0 }}
          </template>
        </div>
      </div>
      <Button
        blue
        :label="feeding ? 'Запуск…' : 'Запустить фидер'"
        :disabled="feeding"
        @click="runFeed"
      />
    </div>
  </div>
</template>
